import React from "react";
import { ratingTier10, formatRating10 } from "../../totw/totwUtils";

const SideHeader = ({
  selectedMatch,
  getPlayerTeam,
  getOpponent,
  formatDate,
}) => {
  const buildImageUrl = (hashOrUrl) => {
    if (!hashOrUrl) {
      return null;
    }

    const value = String(hashOrUrl);

    if (value.startsWith("http://") || value.startsWith("https://")) {
      return value;
    }

    return `https://images.sportdevs.com/${value}.png`;
  };

  // Part D rating unification: ONE shared tier scale + one-decimal format
  // (totwUtils). Null/invalid rating -> the chip is not rendered.
  const headerRatingText = formatRating10(selectedMatch.statistics?.rating);
  const headerRatingTier = headerRatingText
    ? ratingTier10(selectedMatch.statistics?.rating)
    : null;

  const leagueLogo = buildImageUrl(
    selectedMatch.match?.home_team_hash_image || "placeholder"
  );

  return (
    <div className="bg-[#FAFBFC] border-b border-[#E5E6E8] px-4 py-4 sm:px-6 rounded-t-[20px]">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-[#EDF3FB] border-[#C3DBFC] px-3 py-2 rounded-full">
            {leagueLogo && (
              <img
                src={leagueLogo}
                alt={selectedMatch.match?.league_name}
                className="w-5 h-5 object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <span className="text-[#1A212F] font-semibold text-sm">
              {selectedMatch.match?.league_name || "League"}
            </span>
          </div>

          <span className="text-[#6B7280] text-sm">
            {formatDate(selectedMatch.match?.kickoff_at)}
          </span>
        </div>
        {/* Score and Rating */}
        <div className="flex items-center gap-3 md:gap-4 flex-wrap md:flex-nowrap">
          <div className="bg-[#2283FF] text-white px-4 py-2 rounded-lg font-bold text-sm w-full md:w-auto text-center">
            {getPlayerTeam(selectedMatch).score} -{" "}
            {getOpponent(selectedMatch).score}
          </div>
          {headerRatingText && (
            <div
              className="px-4 py-2 rounded-lg flex items-center gap-1.5 w-full md:w-auto justify-center"
              style={{
                backgroundColor: headerRatingTier.bg,
                color: headerRatingTier.fg,
              }}
            >
              <svg
                width="12"
                height="17"
                viewBox="0 0 12 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.47398 9.76L2.66732 15.8333L6.00065 13.8333L9.33398 15.8333L8.52732 9.75333M10.6673 5.83333C10.6673 8.41066 8.57798 10.5 6.00065 10.5C3.42332 10.5 1.33398 8.41066 1.33398 5.83333C1.33398 3.256 3.42332 1.16666 6.00065 1.16666C8.57798 1.16666 10.6673 3.256 10.6673 5.83333Z"
                  stroke={headerRatingTier.fg}
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="font-extrabold text-sm"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {headerRatingText}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Score Section */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Position Badge */}
          {selectedMatch.statistics?.position && (
            <div
              className="bg-[#F5F5F5] rounded-xl px-3 py-2 flex items-center gap-2 border border-[#EDEDED]"
              style={{
                boxShadow: "0px 2px 1px 0px #00000005",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.7952 4.86293C17.4852 3.88082 16.7789 3.05351 15.8577 2.59291L12.4246 0.876495H7.57669L4.14356 2.59321C3.22237 3.0538 2.51632 3.88111 2.20611 4.86323L0.800781 9.3135L4.31191 10.3668L4.99735 8.08122V19.1237H15.0039V8.08122L15.6897 10.3665L19.2008 9.3132L17.7952 4.86293ZM11.9572 1.46512C11.7683 1.91394 11.2673 2.64236 10.0006 2.64236C8.74511 2.64236 8.24007 1.91453 8.047 1.46512H11.9572ZM4.99735 6.76271H4.77839L3.91724 9.63371L1.54215 8.92118L1.75582 8.24485L3.73947 8.8117L3.90134 8.24574L1.93329 7.68331L2.76795 5.0404C3.03018 4.20927 3.62763 3.5091 4.40726 3.11973L4.99735 2.82424V6.76271ZM6.76322 18.5351H5.58598V7.05702V6.76271V2.52993L6.29615 2.1747C6.38444 2.40603 6.53189 2.72506 6.76322 3.06293V18.5351ZM8.23477 18.5351H7.35184V3.74043C7.59553 3.96264 7.88866 4.16424 8.23477 4.32729V18.5351ZM9.70632 18.5351H8.82339V4.54537C9.09063 4.61924 9.38552 4.66604 9.70632 4.68576V18.5351ZM11.1779 18.5351H10.2949V4.68576C10.6157 4.66633 10.9106 4.61954 11.1779 4.54537V18.5351ZM12.6494 18.5351H11.7665V4.32729C12.1126 4.16424 12.4057 3.96264 12.6494 3.74043V18.5351ZM10.0006 4.11391C7.78153 4.11391 7.02545 2.50639 6.8212 1.91217L7.46809 1.58873C7.69353 2.1694 8.33189 3.23098 10.0006 3.23098C11.6694 3.23098 12.3077 2.1694 12.5329 1.58873L13.1783 1.91159C12.969 2.50286 12.1974 4.11391 10.0006 4.11391ZM14.4153 6.76271V7.05702V18.5351H13.238V3.06293C13.4694 2.72506 13.6168 2.40632 13.7051 2.1747L14.4153 2.52993V6.76271ZM15.2229 6.76271H15.0039V2.82424L15.5946 3.11944C16.3739 3.5091 16.9717 4.20927 17.2339 5.04011L18.0686 7.68301L16.1005 8.24544L16.2624 8.8114L18.246 8.24456L18.4597 8.92089L16.0846 9.63341L15.2229 6.76271Z"
                  fill="#2283FF"
                />
              </svg>
              <span className="text-[#1A212F] text-sm font-medium">
                {selectedMatch.statistics.position}
              </span>
            </div>
          )}

          {/* Stats Icons */}
          <div className="flex items-center gap-3 md:gap-4 flex-wrap md:flex-nowrap">
            {/* Goals */}
            <div className="flex items-center gap-1.5">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.9987 14.6667C11.6806 14.6667 14.6654 11.6819 14.6654 7.99999C14.6654 4.3181 11.6806 1.33333 7.9987 1.33333C4.3168 1.33333 1.33203 4.3181 1.33203 7.99999C1.33203 11.6819 4.3168 14.6667 7.9987 14.6667Z"
                  stroke="#2DB500"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M7.9987 12C10.2078 12 11.9987 10.2091 11.9987 7.99999C11.9987 5.79086 10.2078 3.99999 7.9987 3.99999C5.78956 3.99999 3.9987 5.79086 3.9987 7.99999C3.9987 10.2091 5.78956 12 7.9987 12Z"
                  stroke="#2DB500"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M7.9987 9.33333C8.73508 9.33333 9.33203 8.73637 9.33203 7.99999C9.33203 7.26362 8.73508 6.66666 7.9987 6.66666C7.26232 6.66666 6.66536 7.26362 6.66536 7.99999C6.66536 8.73637 7.26232 9.33333 7.9987 9.33333Z"
                  stroke="#2DB500"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span className="text-[#1A212F] font-semibold text-sm">
                {selectedMatch.statistics?.goals || 0}
              </span>
            </div>

            {/* Minutes */}
            <div className="flex items-center gap-1.5">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.9987 3.99999V7.99999L10.6654 9.33333M14.6654 7.99999C14.6654 11.6819 11.6806 14.6667 7.9987 14.6667C4.3168 14.6667 1.33203 11.6819 1.33203 7.99999C1.33203 4.3181 4.3168 1.33333 7.9987 1.33333C11.6806 1.33333 14.6654 4.3181 14.6654 7.99999Z"
                  stroke="#8F8F8F"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span className="text-[#1A212F] font-semibold text-sm">
                {selectedMatch.statistics?.minutes_played || 0}' Minutes Played
              </span>
            </div>

            {/* Yellow Cards */}
            {(selectedMatch.statistics?.yellow_cards || 0) > 0 && (
              <div className="flex items-center gap-1.5 bg-[#FFC6541A] px-2 py-1 rounded-lg">
                <div
                  className=" w-4 h-5 rounded-sm"
                  style={{
                    background:
                      "linear-gradient(180deg, #FFC654 0%, #FFDE9C 100%)",
                  }}
                ></div>
                <span className="text-[#1A212F] font-semibold text-sm">
                  {selectedMatch.statistics?.yellow_cards || 0}
                </span>
              </div>
            )}

            {/* Red Cards */}
            {(selectedMatch.statistics?.red_cards || 0) > 0 && (
              <div className="flex items-center gap-1.5 bg-[#EF44441A] px-2 py-1 rounded-lg">
                <div
                  className="w-4 h-5 rounded-sm"
                  style={{
                    background:
                      "linear-gradient(180deg, #FF4D4F 0%, #FF8183 100%)",
                  }}
                ></div>
                <span className="text-[#1A212F] font-semibold text-sm">
                  {selectedMatch.statistics?.red_cards || 0}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SideHeader;
